/**
 * Fetches guide manifest and markdown content from public/guides.
 * Content is served as static files; no backend required.
 */

import type { GuideMeta, GuidesManifest } from '../types/guide';

// Cache-bust so updated guides show after deploy without hard refresh
const GUIDES_VERSION = '7';
const MANIFEST_URL = `/guides/manifest.json?v=${GUIDES_VERSION}`;
const CONTENT_BASE = '/guides/content';

let manifestCache: GuidesManifest | null = null;

export async function fetchGuidesManifest(): Promise<GuidesManifest> {
  if (manifestCache) return manifestCache;
  const res = await fetch(MANIFEST_URL, { cache: 'no-store' });
  if (!res.ok) throw new Error('Failed to load guides manifest');
  const data: GuidesManifest = await res.json();
  manifestCache = data;
  return data;
}

export async function getGuideBySlug(slug: string): Promise<GuideMeta | null> {
  const { guides } = await fetchGuidesManifest();
  return guides.find((g) => g.slug === slug) ?? null;
}

export async function fetchGuideContent(slug: string): Promise<string> {
  const res = await fetch(`${CONTENT_BASE}/${slug}.md?v=${GUIDES_VERSION}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Guide content not found');
  return res.text();
}

export function getGuideCanonicalUrl(slug: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://genanything.ai';
  return `${origin}/guides/${slug}`;
}

export function getGuideOgImageUrl(guide: GuideMeta): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://genanything.ai';
  if (guide.ogImage?.startsWith('http')) return guide.ogImage;
  const path = guide.ogImage?.startsWith('/') ? guide.ogImage : (guide.ogImage ? `/guides/og/${guide.ogImage}` : '/logo-icon.png');
  return `${origin}${path}`;
}
